import React, { useEffect, useMemo, useRef } from "react"
import { Box, Typography } from "@mui/material"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { DEFAULT_BEHAVIOR } from "@/shared/components/image-gallery/constants"
import { useGalleryNavigation } from "@/shared/components/image-gallery/hooks/useGalleryNavigation"
import { useGalleryKeyboardNavigation } from "@/shared/components/image-gallery/hooks/useGalleryKeyboardNavigation"
import { useGalleryMediaActions } from "@/shared/components/image-gallery/hooks/useGalleryMediaActions"
import GalleryModalShell from "@/shared/components/image-gallery/components/GalleryModalShell"
import ThumbnailStrip from "@/shared/components/image-gallery/components/ThumbnailStrip"
import MainImageViewport from "@/shared/components/image-gallery/components/MainImageViewport"
import GalleryControls from "@/shared/components/image-gallery/components/GalleryControls"
import ProgressIndicator from "@/shared/components/image-gallery/components/ProgressIndicator"
import { ImageGalleryItem, ImageGalleryProps } from "@/shared/components/image-gallery/types"

const toItems = (props: ImageGalleryProps): ImageGalleryItem[] => {
  if ("items" in props && props.items) return props.items.filter(item => item?.url)
  return props.images.filter(Boolean).map((url, index) => ({
    url,
    alt: props.imageAlts?.[index]
  }))
}

/**
 * Preferred API: pass `items` with `{ url, alt?, id?, metadata? }` + `initialIndex`.
 * Legacy API is still supported for migration: `images` + optional `imageAlts`.
 */
const ImageGallery: React.FC<ImageGalleryProps> = props => {
  const {
    onClose,
    initialIndex = 0,
    wrapNavigation = DEFAULT_BEHAVIOR.wrapNavigation,
    showThumbnails = DEFAULT_BEHAVIOR.showThumbnails,
    showProgress = DEFAULT_BEHAVIOR.showProgress,
    showShareButton = DEFAULT_BEHAVIOR.showShareButton,
    showDownloadButton = DEFAULT_BEHAVIOR.showDownloadButton,
    regenerationHandlers
  } = props

  const { showSnackbar } = useSnackbar()
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const items = useMemo(() => toItems(props), [props])

  const { currentIndex, goToIndex, goToPrev, goToNext, isPrevDisabled, isNextDisabled } = useGalleryNavigation({
    itemsLength: items.length,
    initialIndex,
    wrapNavigation
  })

  useGalleryKeyboardNavigation({
    enabled: items.length > 0,
    onPrev: goToPrev,
    onNext: goToNext,
    onClose
  })

  const { isDownloading, handleDownload, handleShare } = useGalleryMediaActions({
    items,
    currentIndex,
    onError: message => showSnackbar(message, "error"),
    onInfo: message => showSnackbar(message, "info")
  })

  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  if (items.length === 0) {
    return (
      <GalleryModalShell onClose={onClose}>
        <Box sx={{ m: "auto", p: 3, bgcolor: "background.paper", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No images available to preview.
          </Typography>
        </Box>
      </GalleryModalShell>
    )
  }

  const currentItem = items[currentIndex]
  if (!currentItem) return null

  const isRegenerating = regenerationHandlers?.isRegenerating?.(currentItem, currentIndex) ?? false
  const regenerateDisabledReason =
    regenerationHandlers?.getRegenerateDisabledReason?.(currentItem, currentIndex) ?? null

  return (
    <GalleryModalShell onClose={onClose}>
      {showThumbnails && <ThumbnailStrip items={items} currentIndex={currentIndex} onSelect={goToIndex} />}

      <Box sx={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <GalleryControls
          closeButtonRef={closeButtonRef}
          onClose={onClose}
          onPrev={goToPrev}
          onNext={goToNext}
          onDownload={handleDownload}
          onShare={handleShare}
          isDownloading={isDownloading}
          isPrevDisabled={isPrevDisabled}
          isNextDisabled={isNextDisabled}
          showShareButton={showShareButton}
          showDownloadButton={showDownloadButton}
          onRegenerateSame={
            regenerationHandlers?.onRegenerateSame
              ? () => regenerationHandlers.onRegenerateSame?.(currentItem, currentIndex)
              : undefined
          }
          onRegenerateDifferent={
            regenerationHandlers?.onRegenerateDifferent
              ? () => regenerationHandlers.onRegenerateDifferent?.(currentItem, currentIndex)
              : undefined
          }
          isRegenerating={isRegenerating}
          regenerateDisabledReason={regenerateDisabledReason}
        />

        <MainImageViewport item={currentItem} index={currentIndex} total={items.length} />

        {showProgress && <ProgressIndicator total={items.length} currentIndex={currentIndex} onSelect={goToIndex} />}
      </Box>
    </GalleryModalShell>
  )
}

export default ImageGallery
export type { ImageGalleryProps, ImageGalleryItem }
